import { MSG_SESSION_OVER } from "@services/http/useApiRequest";
import store_ from "@store";
import { logout } from "@store/auth/actions";
import { addTokenExpiresDays, getAuthToken } from "@store/auth/cookieStorage";
import dbg from "@utils/dbg";
import { toasts } from "@utils/hooks/useToasts";
import { AnyAction } from "redux";
import * as wlNet from "wl-net";
import { IS_DEBUG, REACT_APP_BASE_URL, WITH_CREDENTIALS } from "../../../config";

export type BaseResponse = wlNet.BaseResponse;
export const ServerErrorCodes = wlNet.ServerErrorCodes;
export * from "./createRequestControl";

export function createTyper<
  Payload extends any = any,
  Args extends any[] = any[],
  Response extends BaseResponse = BaseResponse,
>(typer: {
  makeRequest(
    handle: wlNet.RequestHandle,
    ...args: Args
  ): wlNet.RequestArgs<Payload> | void;
  makeResponse?(r: BaseResponse): Response;
}): wlNet.RequestTyper<BaseResponse, Payload, Args, Response> {
  return typer;
}

export interface RequestOptions3 {
  readonly forceToast?: boolean;
  readonly email?: string;
  readonly password?: string;
  onError?: (error: wlNet.APIError, params?: unknown[]) => void;
}

export const showAPIErrorAndLogout = (
  message: string,
  _?: boolean,
  __?: boolean,
  skipToasts?: boolean
) => {
  if (!skipToasts) toasts.error(message);
  setTimeout(() => {
    store_()?.dispatch(logout(message) as unknown as AnyAction)
  }, 900);
};

const processAPIError3 = (
  e: wlNet.APIError,
  options?: RequestOptions3,
  params?: unknown[]
) => {
  if (!(e instanceof wlNet.APIError)) throw e;
  if (options?.onError) return options.onError(e, params);
  switch (e.code) {
    case ServerErrorCodes.Cancelled:
      // show nothing
      break;
    case ServerErrorCodes.Unknown:
      if (e.status && e.status !== 200) {
        toasts.error(e.message);
      }
      break;
    case ServerErrorCodes.Need2FA:
      options?.onError?.(e, [options, ...(params ?? [])]);      
      break;
    case ServerErrorCodes.NeedLogon:
      showAPIErrorAndLogout(MSG_SESSION_OVER, options?.forceToast, true, true);
      break;
    case ServerErrorCodes.PermissionDenied:
      toasts.error(e.message);
      break;
    default:
      toasts.error(e.message);
  }
};

const createAPIService = () => {
  const svc: wlNet.APIService = wlNet.createAPIService({
    isDebug: IS_DEBUG,
    baseURL: REACT_APP_BASE_URL + "/api",
    withCredentials: WITH_CREDENTIALS,
    touchToken() {
      const token = getAuthToken();
      if (token) {
        addTokenExpiresDays(2);
        return token;
      } else {
        dbg.log("no token!!");
      }
    },
  });

  const req = wlNet.TypedRequesters<BaseResponse>(svc);

  const withOptions = (options?: RequestOptions3) => {
    const createRequest: typeof req.createRequest = (typer) => {
      const _r = req.createRequest(typer);
      const request: typeof _r = async (...params) => {
        const procErr = (e: unknown) =>
          processAPIError3(e as wlNet.APIError, options, params);
        const r = await _r(...params).catch(procErr);
        const err = svc.getLastError();
        if (err) {
          procErr(err);
          return;
        }
        return r;
      };
      return request;
    };

    const createBlobRequest: typeof req.createBlobRequest = (typer) => {
      const _r = req.createBlobRequest(typer);
      const request: typeof _r = async (...params) => {
        const procErr = (e: unknown) =>
          processAPIError3(e as wlNet.APIError, options, params);
        const r = await _r(...params).catch(procErr);
        const err = svc.getLastError();
        if (err) {
          procErr(err);
          return;
        }
        return r;
      };
      return request;
    };

    return { createRequest, createBlobRequest };
  };

  const withErrorHandler = (
    onError: (error: wlNet.APIError, params?: unknown[]) => void
  ) => withOptions({ onError });

  return {
    ...svc,
    createRequest: withOptions().createRequest,
    createBlobRequest: withOptions().createBlobRequest,
    getImage: svc.getBlob,
    withOptions,
    withErrorHandler,
  };
};

const apiService = createAPIService();

export default apiService;
